import {
  type BoardChunkMessage,
  type BoardScenePayload,
  CHUNK_BYTES,
  MAX_SCENE_BYTES,
} from './types'

const encoder = new TextEncoder()

/** Thrown when a board has grown past what we are willing to put on the wire. */
export class SceneTooLargeError extends Error {
  readonly bytes: number

  constructor(bytes: number) {
    super(`Board scene is ${bytes} bytes, over the ${MAX_SCENE_BYTES} limit`)
    this.name = 'SceneTooLargeError'
    this.bytes = bytes
  }
}

/**
 * Split a scene into reliable-packet-sized chunks.
 *
 * Sliced on ENCODED BYTES rather than string length. The first version cut at
 * 12,000 characters, which is fine for ASCII and roughly double the budget the
 * moment a teacher writes Arabic — the packet then exceeded LiveKit's reliable cap
 * and the publish failed, so the board stopped syncing. Arabic is our default
 * language, so that was a matter of time rather than an edge case. (`useSurface`
 * now surfaces a failed chunk as `boardError`; it used to vanish entirely.)
 */
export function chunkScene(
  scene: BoardScenePayload,
  meta: { id: string; v: number; by: string },
): BoardChunkMessage[] {
  const raw = JSON.stringify(scene)
  const totalBytes = encoder.encode(raw).length
  if (totalBytes > MAX_SCENE_BYTES) throw new SceneTooLargeError(totalBytes)

  const parts = splitByBytes(raw, CHUNK_BYTES)
  if (parts.length === 0) parts.push(JSON.stringify({ elements: [] }))
  return parts.map((body, i) => ({
    kind: 'board_chunk' as const,
    id: meta.id,
    v: meta.v,
    i,
    n: parts.length,
    by: meta.by,
    body,
  }))
}

/**
 * Split on code-point boundaries so no chunk ends mid-character — cutting a
 * surrogate pair or a multi-byte sequence in half would make the rejoined JSON
 * unparseable at the far end.
 *
 * Exported: chat snapshots chunk with exactly the same rules (and the same
 * Arabic-bytes lesson) — see src/chat/useChat.ts.
 */
export function splitByBytes(input: string, budget: number): string[] {
  const parts: string[] = []
  let current = ''
  let currentBytes = 0

  // Iterating a string yields whole code points, so surrogate pairs stay together.
  for (const char of input) {
    const size = charBytes(char)
    if (currentBytes + size > budget && current.length > 0) {
      parts.push(current)
      current = ''
      currentBytes = 0
    }
    current += char
    currentBytes += size
  }
  if (current.length > 0) parts.push(current)
  return parts
}

/** UTF-8 length of one code point, without allocating a buffer per character. */
function charBytes(char: string): number {
  const code = char.codePointAt(0) ?? 0
  if (code < 0x80) return 1
  if (code < 0x800) return 2
  if (code < 0x10000) return 3
  return 4
}

/**
 * Reassembles chunked scenes.
 *
 * Reliable packets arrive intact but not necessarily in order, so parts are
 * placed by index and only joined once every slot is filled.
 */
export class ChunkAssembler {
  /**
   * Bounded so an abandoned transfer cannot accumulate. A teacher who closes the
   * board mid-send, or a sender that drops between chunks, otherwise leaves its
   * partial buffer in memory for the rest of a 90-minute lesson.
   */
  private static readonly MAX_PENDING = 4

  private buffers = new Map<
    string,
    { n: number; parts: (string | undefined)[]; v: number; by: string }
  >()

  push(chunk: BoardChunkMessage): { v: number; by: string; json: string } | null {
    if (chunk.n < 1 || chunk.i < 0 || chunk.i >= chunk.n) return null
    let entry = this.buffers.get(chunk.id)
    if (!entry || entry.n !== chunk.n) {
      entry = {
        n: chunk.n,
        parts: Array.from({ length: chunk.n }),
        v: chunk.v,
        by: chunk.by,
      }
      this.buffers.set(chunk.id, entry)
      this.evictOldest()
    }
    entry.parts[chunk.i] = chunk.body
    entry.v = chunk.v
    entry.by = chunk.by
    if (entry.parts.some((part) => part === undefined)) return null
    this.buffers.delete(chunk.id)
    return { v: entry.v, by: entry.by, json: entry.parts.join('') }
  }

  /** Map iteration is insertion-ordered, so the first key is the oldest transfer. */
  private evictOldest(): void {
    while (this.buffers.size > ChunkAssembler.MAX_PENDING) {
      const oldest = this.buffers.keys().next().value
      if (oldest === undefined) return
      this.buffers.delete(oldest)
    }
  }

  clear(): void {
    this.buffers.clear()
  }
}
